import React from 'react';
import { Handle, Position } from 'reactflow';
import {
  AccountTree,
  Public,
  Cloud,
  Router,
  Security,
  Computer,
  Add,
} from '@mui/icons-material';
import { Tooltip, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles"; 
import PropTypes from 'prop-types';

const getNodeIcon = (type) => {
  switch (type) {
    case 'account': return AccountTree;
    case 'region': return Public;
    case 'vpc': return Cloud;
    case 'subnet': return Router;
    case 'securityGroup': return Security;
    case 'ec2Instance': return Computer;
    default: return AccountTree;
  }
};

const getNodeStyles = (type, isSelected, theme) => {
  const isDark = theme.palette.mode === 'dark';
  
  const colors = {
    account: isDark ? '#66b2ff' : '#0070f3',
    region: isDark ? '#69f0ae' : '#00b894',
    vpc: isDark ? '#ffe57f' : '#fdcb6e',
    subnet: isDark ? '#ffab91' : '#e17055',
    securityGroup: isDark ? '#b39ddb' : '#6c5ce7',
    ec2Instance: isDark ? '#64b5f6' : '#0984e3',
  };

  const color = colors[type] || (isDark ? '#b0bec5' : '#636e72');

  return {
    padding: '12px 16px',
    borderRadius: '8px',
    border: `2px solid ${color}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '140px',
    textAlign: 'center',
    fontWeight: '500',
    boxShadow: isSelected 
      ? (isDark ? `0 4px 12px ${color}66` : '0 4px 12px rgba(0,0,0,0.15)') 
      : (isDark ? '0 2px 6px rgba(0,0,0,0.4)' : '0 2px 6px rgba(0,0,0,0.1)'),
    backgroundColor: isSelected ? color : theme.palette.background.paper,
    color: isSelected ? (isDark ? '#121212' : '#ffffff') : color,
    position: 'relative',
  };
};

const CloudNode = ({ data }) => {
  const theme = useTheme(); //  Initialize theme
  const isDark = theme.palette.mode === 'dark';

  const handleClick = (e) => {
    if (typeof data.onClick === "function") {
      data.onClick(data.id, data.type, e);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  const IconComponent = getNodeIcon(data.type);

  // Helper to format the type label
  const formattedType = data.type
    .replace('securityGroup', 'Security Group')
    .replace('ec2Instance', 'EC2 Instance');

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ width: 12, height: 12 }} />

      <Tooltip
        title={
          <Box>
            <Typography variant="caption" display="block"><b>ID:</b> {data.id}</Typography>
            <Typography variant="caption" display="block"><b>Type:</b> {formattedType}</Typography>
          </Box>
        }
        arrow
        placement="top"
      >
        <div 
          style={getNodeStyles(data.type, data.isSelected, theme)} 
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          aria-label={`${formattedType} node: ${data.label}`}
          aria-expanded={data.hasChildren ? !!data.isExpanded : undefined}
        >
          {/* Expand/Collapse Indicator */}
          {data.hasChildren && (
            <Box
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                width: 24,
                height: 24,
                backgroundColor: isDark ? '#90caf9' : '#0070f3',
                color: isDark ? '#121212' : '#ffffff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isDark ? '0px 2px 4px rgba(0,0,0,0.5)' : 'none',
              }}
            >
              <Add
                sx={{
                  fontSize: 16,
                  transform: data.isExpanded ? 'rotate(45deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '1px' }}>
            <IconComponent sx={{ fontSize: 24 }} />
          </Box>

          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2 }}>
            {data.label}
          </Typography>
          
          <Typography sx={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '4px', textTransform: 'capitalize' }}>
            {formattedType}
          </Typography>
        </div>
      </Tooltip>

      <Handle type="source" position={Position.Bottom} style={{ width: 12, height: 12 }} />
    </>
  );
};

CloudNode.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    label: PropTypes.string,
    isSelected: PropTypes.bool,
    hasChildren: PropTypes.bool,
    isExpanded: PropTypes.bool,
  }).isRequired,
};

export default CloudNode;